import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["template", "gallery", "input", "persisted", "unpersisted", "notice", "gallerySection"];
  static classes = ["supportedInput"];

  connect() {
    this.fileSizeBase = 1000;
    this.inputTarget.classList.add(...this.supportedInputClasses);
  }

  previewInGallery() {
    if (this.inputTarget.files) {
      let inputFiles = this.inputTarget.files;

      this.unpersistedTargets.forEach((n) => n.remove());
      this.notices = {};
      this.noticeTarget.innerHTML = "";

      if (!this._validateFiles(inputFiles)) {
        this._showNoticeForFiles();
        return false;
      }

      Array.from(inputFiles).forEach((file) => {
        if (this._validateFile(file)) {
          this.gallerySectionTarget.classList.remove("hidden");
          this._addFile(this.galleryTarget, file);
        } else {
          this._showNoticeForFile(file);
        }
      });
    }
  }

  removeFile(event) {
    const target = this.unpersistedTargets.find((li) => li.contains(event.currentTarget));
    const index = this.unpersistedTargets.indexOf(target);
    const attachments = this.inputTarget.files;
    let fileBuffer = new DataTransfer();

    // append the file list to an array iteratively
    for (let i = 0; i < attachments.length; i++) {
      if (index !== i) fileBuffer.items.add(attachments[i]);
    }

    // Assign buffer to file input
    this.inputTarget.files = fileBuffer.files;
    target.remove();
    if (fileBuffer.files.length == 0) {
      this.gallerySectionTarget.classList.add("hidden");
    }
  }

  removeAll() {
    var fileFieldHTML = this.inputTarget.outerHTML;
    var parent = this.inputTarget.parentElement;
    this.inputTarget.remove();
    parent.insertAdjacentHTML("afterbegin", fileFieldHTML);
    this.unpersistedTargets.forEach((n) => n.remove());
    this.gallerySectionTarget.classList.add("hidden");
  }

  _validateFiles(files) {
    const validFilesTotalCount = this._validateFilesTotalCount(files);

    return validFilesTotalCount;
  }

  _validateFile(file) {
    const validFileSize = this._validateFileSize(file);
    const validFileType = this._validateFileType(file);

    return validFileSize && validFileType;
  }

  _validateFileType(file) {
    const acceptedTypes = this.inputTarget.accept.replace(/\s/g, "").split(",");
    const validations = acceptedTypes.map((type) => new RegExp(type.replace("*", ".*")));
    const isValid = validations.some((validation) => validation.test(file.type));

    if (isValid) {
      return true;
    } else {
      this.notices[file.name] ||= [];
      this.notices[file.name].push(`"${file.type}" is not an allowed type`);
      return false;
    }
  }

  _validateFileSize(file) {
    const maxFileSize = this.inputTarget.getAttribute("max-file-size");
    if (!maxFileSize) return true;

    const base = this.fileSizeBase;
    const toInt = (value) => parseInt(value, 10);

    const maxFileSizeNatural = maxFileSize.trim();
    let maxFileSizeString = maxFileSizeNatural;
    let maxFileSizeBytes = null;

    if (/MB$/i.test(maxFileSizeString)) {
      maxFileSizeString = maxFileSizeString.replace(/MB$i/, "").trim();
      maxFileSizeBytes = toInt(maxFileSizeString) * base * base;
    } else if (/KB$/i.test(maxFileSizeString)) {
      maxFileSizeString = maxFileSizeString.replace(/KB$i/, "").trim();
      maxFileSizeBytes = toInt(maxFileSizeString) * base;
    } else {
      maxFileSizeBytes = toInt(maxFileSizeString);
    }

    const isValid = maxFileSizeBytes !== null && file.size <= maxFileSizeBytes;
    if (isValid) {
      return true;
    } else {
      this.notices[file.name] ||= [];
      this.notices[file.name].push(
        `${this._humanFileSize(file.size)} is larger than the ${maxFileSizeNatural} allowed`,
      );
      return false;
    }
  }

  _validateFilesTotalCount(files) {
    const maxFilesCount = this.inputTarget.getAttribute("max-files-count");
    if (!maxFilesCount) return true;
    const filesCount = files.length;
    const isValid = filesCount <= maxFilesCount;

    if (isValid) {
      return true;
    } else {
      this.notices["files"] ||= [];
      this.notices["files"].push(`${filesCount} is more than the ${maxFilesCount} allowed`);
      return false;
    }
  }

  _addFile(target, file) {
    const objectURL = URL.createObjectURL(file);
    const clone = this.templateTarget.content.cloneNode(true);
    const nameElement = clone.querySelector("[data-target='name']");
    const sizeElement = clone.querySelector("[data-target='size']");
  
    nameElement.textContent = file.name;
    nameElement.title = file.name;
    sizeElement.textContent = this._humanFileSize(file.size);
  
    const imgElement = clone.querySelector("img");
    const imgContainer = imgElement.parentNode;
  
    if (file.type.startsWith('image/')) {
      Object.assign(imgElement, {
        src: objectURL,
        alt: file.name,
      });
      imgElement.style.display = '';
    } else {
      imgElement.style.display = 'none';
      const iconElement = document.createElement('div');
      iconElement.style.alignContent = 'center';
      iconElement.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" fill="currentColor" class="bi bi-file-earmark-fill m-auto text-io-purple" viewBox="0 0 16 16">
          <path d="M4 0h5.293A1 1 0 0 1 10 .293L13.707 4a1 1 0 0 1 .293.707V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2m5.5 1.5v2a1 1 0 0 0 1 1h2z"/>
        </svg>
      `;
      imgContainer.appendChild(iconElement);
    }
    target.append(clone);
  }

  _showNoticeForFiles() {
    const li = document.createElement("li");
    li.innerHTML = `
      <code>Please try again&hellip;</code>
      <ul class="list-disc ml-8">
        ${this.notices["files"].map((notice) => `<li>${notice}</li>`).join("")}
      </ul>
    `;
    this.noticeTarget.appendChild(li);
  }

  _showNoticeForFile(file) {
    const li = document.createElement("li");
    li.innerHTML = `
      <code>${file.name}</code>
      <ul class="list-disc ml-8">
        ${this.notices[file.name].map((notice) => `<li>${notice}</li>`).join("")}
      </ul>
    `;
    this.noticeTarget.appendChild(li);
  }

  _humanFileSize(size) {
    const base = this.fileSizeBase;
    const i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(base));
    const suffix = ["B", "KB", "MB", "GB"][i];
    const integer = Math.round(size / Math.pow(base, i));
    return integer + " " + suffix;
  }
}
